import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MdAutoAwesome, MdExpandMore, MdMemory } from 'react-icons/md';
import { useQuery } from '@tanstack/react-query';
import useOnClickOutside from '@hooks/useOnClickOutside';
import { listLlmModels, type LlmModelOption } from '@api/llmModels';
import { LLM_MODEL_AUTO } from '@utils/llmModel';
import {
  ApprovalPickerContainer,
  ApprovalPickerTrigger,
  ApprovalPickerMenu,
  ApprovalPickerItem,
  ApprovalPickerItemContent,
  ApprovalPickerItemLabel,
  ApprovalPickerItemDesc,
} from './Chat.styled';

interface LlmModelPickerProps {
  llmModel: string;
  onPick: (model: string) => void;
  disabled?: boolean;
  menuDirection?: 'up' | 'down';
  /** `field` renders a full-width form control instead of the compact chat chip. */
  appearance?: 'inline' | 'field';
}

const LlmModelPicker: React.FC<LlmModelPickerProps> = ({
  llmModel,
  onPick,
  disabled,
  menuDirection = 'up',
  appearance = 'inline',
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const ref = useOnClickOutside<HTMLDivElement>(() => setOpen(false));

  const { data: models = [] } = useQuery<LlmModelOption[]>({
    queryKey: ['llmModels'],
    queryFn: listLlmModels,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  if (models.length === 0) return null;

  const autoLabel = intl.formatMessage({ id: 'chat.model.auto.label', defaultMessage: 'Auto (recommended)' });
  const selected = models.find((m) => m.value === llmModel);
  const isAuto = !selected;
  const listed = models.filter((m) => !m.deprecated || m === selected);
  const field = appearance === 'field';
  const labelOf = (model: LlmModelOption) =>
    model.deprecated
      ? intl.formatMessage(
          { id: 'chat.model.deprecated.label', defaultMessage: '{label} (deprecated)' },
          { label: model.label },
        )
      : model.label;
  const descriptionOf = (model: LlmModelOption) =>
    model.deprecated
      ? intl.formatMessage({
          id: 'chat.model.deprecated.desc',
          defaultMessage: 'No longer offered. It keeps running until you pick another model.',
        })
      : model.description;

  const handlePick = (model: string) => {
    onPick(model);
    setOpen(false);
  };

  return (
    <ApprovalPickerContainer ref={ref} $field={field}>
      <ApprovalPickerTrigger
        type="button"
        $field={field}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        data-testid="llm-model-picker"
        title={intl.formatMessage({ id: 'chat.model.title', defaultMessage: 'Choose the AI model for this conversation' })}
      >
        {isAuto ? <MdAutoAwesome /> : <MdMemory />}
        {isAuto ? autoLabel : labelOf(selected)}
        <MdExpandMore />
      </ApprovalPickerTrigger>
      {open && (
        <ApprovalPickerMenu $menuDirection={menuDirection} $field={field}>
          <ApprovalPickerItem
            $selected={isAuto}
            onClick={() => handlePick(LLM_MODEL_AUTO)}
            data-testid="llm-model-option-auto"
          >
            <MdAutoAwesome />
            <ApprovalPickerItemContent>
              <ApprovalPickerItemLabel>{autoLabel}</ApprovalPickerItemLabel>
              <ApprovalPickerItemDesc>
                {intl.formatMessage({ id: 'chat.model.auto.desc', defaultMessage: 'The model we pick for you, always at the 1x credit rate' })}
              </ApprovalPickerItemDesc>
            </ApprovalPickerItemContent>
          </ApprovalPickerItem>
          {listed.map((model) => {
            const description = descriptionOf(model);
            return (
              <ApprovalPickerItem
                key={model.value}
                $selected={model.value === llmModel}
                onClick={() => handlePick(model.value)}
                data-testid={`llm-model-option-${model.value}`}
              >
                <MdMemory />
                <ApprovalPickerItemContent>
                  <ApprovalPickerItemLabel>{labelOf(model)}</ApprovalPickerItemLabel>
                  {description && <ApprovalPickerItemDesc>{description}</ApprovalPickerItemDesc>}
                </ApprovalPickerItemContent>
              </ApprovalPickerItem>
            );
          })}
        </ApprovalPickerMenu>
      )}
    </ApprovalPickerContainer>
  );
};

export default LlmModelPicker;
